import { Link, useLocation } from 'react-router-dom';
import { House, KeyRound, Ticket, Clock, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatBadgeCount } from '@/lib/notificationBadge';
import { useTicketAdmin } from '@/hooks/useTicketAdmin';
import { useInvitations } from '@/hooks/useInvitations';

/**
 * BottomNav — floating glass pill.
 *
 * Flat icon row (smart-home style): the active item expands into a solid
 * primary pill with its label; inactive items are muted icons. Floats
 * above the safe area as a rounded island, matching the header pill.
 *
 * Destinations: Hem (välkomstsida) → Nycklar (enheter + nycklar med
 * anläggningsmeny) → Ärenden (endast ärendeadmin-roller — UX-gate,
 * RLS är behörighetsgränsen) → Historik. Tilldela nyckel nås från
 * nyckelpanelen (/send-key), och Bokningar bor kvar som route
 * (/book/:resourceId) — ingen av dem ligger i primärnavigeringen.
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  badgeCount?: number;
}

export function BottomNav() {
  const { pathname } = useLocation();
  const { isTicketAdmin } = useTicketAdmin();
  const { data: invitations } = useInvitations();
  const invitationCount = invitations?.length ?? 0;

  // Hide on auth pages
  if (pathname?.startsWith('/auth') || pathname?.startsWith('/bankid-callback')) return null;

  const navItems: NavItem[] = [
    { to: '/', label: 'Hem', icon: House, exact: true, badgeCount: invitationCount },
    { to: '/keys', label: 'Nycklar', icon: KeyRound },
    ...(isTicketAdmin ? [{ to: '/admin/arenden', label: 'Ärenden', icon: Ticket }] : []),
    { to: '/history', label: 'Historik', icon: Clock },
  ];

  return (
    <nav
      className="fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100vw-1.25rem)] max-w-[420px]"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
      aria-label="Huvudmeny"
    >
      <div className="glass-regular rounded-full px-2 py-2">
        <div className="flex items-center justify-between">
          {navItems.map(({ to, label, icon: Icon, exact, badgeCount }) => {
            const isActive = exact ? pathname === to : pathname?.startsWith(to);
            // Badgen döljs på aktiv flik — innehållet syns då direkt på sidan.
            const badge = isActive ? null : formatBadgeCount(badgeCount ?? 0);
            const ariaLabel =
              badgeCount && badgeCount > 0
                ? `${label}, ${badgeCount === 1 ? '1 ny inbjudan' : `${badgeCount} nya inbjudningar`}`
                : label;
            return (
              <Link
                key={to}
                to={to}
                aria-label={ariaLabel}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'relative flex items-center justify-center gap-1.5 rounded-full transition-all duration-300 active:scale-95',
                  isActive
                    ? 'bg-primary text-primary-foreground px-4 py-2.5 shadow-[0_8px_24px_-8px_hsl(var(--glass-shadow))]'
                    : 'px-3 py-2.5 text-muted-foreground hover:text-foreground hover:scale-105'
                )}
              >
                <Icon className={cn('h-5 w-5', isActive ? 'stroke-[2]' : 'stroke-[1.5]')} />
                {isActive && (
                  <span className="text-xs font-semibold leading-none whitespace-nowrap">
                    {label}
                  </span>
                )}
                {badge && (
                  <span
                    aria-hidden="true"
                    className="absolute -top-0.5 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground ring-2 ring-background"
                  >
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
